import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, Users, ListChecks, PlayCircle, Phone } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, SectionLabel } from "../components/PageHeader";
import { Reveal } from "../lib/motionx";
import { services, site } from "../data/site";
import { useLang } from "../i18n";

const steps = [
  { n: "01", title: "Reach out", body: "Call us or submit a request through the contact form — whatever's easiest for you." },
  { n: "02", title: "Talk it through", body: "We'll listen to your questions and help you understand possible next steps." },
  { n: "03", title: "Begin together", body: "When you're ready, we'll guide you through getting started, at your family's pace." },
];

export default function Services() {
  const { t } = useLang();
  const { hash } = useLocation();
  useSeo({
    title: "Services | Speech, ABA & Developmental Care | Amenity Health Services",
    description:
      "Explore the pediatric services offered by Amenity Health Services — speech therapy, ABA, occupational therapy and more for families in Houston, TX.",
  });

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [hash]);

  return (
    <>
      <PageHeader
        label="Services"
        titleLines={["Support that meets", "children where they are"]}
        intro="Each of our services is designed around the child — playful, patient, and shaped by your family's goals."
      />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 space-y-6">
        {services.map((s, i) => (
          <Reveal key={s.slug} delay={0.05}>
            <article
              id={s.slug}
              data-testid={`service-section-${s.slug}`}
              className="scroll-mt-28 rounded-[2rem] border border-black/5 bg-white overflow-hidden grid lg:grid-cols-2"
            >
              <div className={`relative min-h-[280px] ${i % 2 ? "lg:order-2" : ""}`}>
                <img src={s.image} alt={s.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              </div>
              <div className="p-8 sm:p-12">
                <span className="text-xs font-medium uppercase tracking-[0.2em] text-teal">
                  {t("Service")} {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-3 font-heading font-semibold text-2xl sm:text-3xl tracking-tight text-ink">
                  {t(s.name)}
                </h2>
                <p className="mt-3 text-ink/65 leading-relaxed">{t(s.short)}</p>

                <div className="mt-6 space-y-5">
                  <div>
                    <h3 className="flex items-center gap-2 font-heading font-semibold text-ink text-sm uppercase tracking-wide">
                      <Users size={16} className="text-teal" /> {t("Who it's for")}
                    </h3>
                    <p className="mt-1.5 text-sm text-ink/60 leading-relaxed">{t(s.for)}</p>
                  </div>
                  <div>
                    <h3 className="flex items-center gap-2 font-heading font-semibold text-ink text-sm uppercase tracking-wide">
                      <ListChecks size={16} className="text-teal" /> {t("What it includes")}
                    </h3>
                    <ul className="mt-2 space-y-1.5">
                      {s.includes.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-ink/65">
                          <Check size={16} className="text-sage mt-0.5 shrink-0" /> {t(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="flex items-center gap-2 font-heading font-semibold text-ink text-sm uppercase tracking-wide">
                      <PlayCircle size={16} className="text-teal" /> {t("How to get started")}
                    </h3>
                    <p className="mt-1.5 text-sm text-ink/60 leading-relaxed">{t(s.start)}</p>
                  </div>
                </div>

                <Link
                  to="/contact"
                  data-testid={`service-request-btn-${s.slug}`}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3.5 font-medium text-white hover:bg-[#2E6088] transition-colors"
                >
                  {t("Request Information")} <ArrowRight size={17} />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      {/* GETTING STARTED */}
      <section className="py-16 sm:py-24 bg-sage/25">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel className="mb-5">{t("Getting started")}</SectionLabel>
            <h2 className="font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-ink max-w-2xl">
              {t("How someone can begin services")}
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-black/5 bg-cream p-8">
                  <span className="font-heading font-light text-5xl text-teal/30">{s.n}</span>
                  <h3 className="mt-4 font-heading font-semibold text-xl text-ink">{t(s.title)}</h3>
                  <p className="mt-2 text-ink/60 leading-relaxed">{t(s.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="services-getstarted-request-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-4 font-medium text-white hover:bg-[#2E6088] transition-colors"
              >
                {t("Request Information")} <ArrowRight size={18} />
              </Link>
              <a
                href={site.phoneHref}
                data-testid="services-getstarted-call-btn"
                className="inline-flex items-center gap-2 rounded-full border border-teal/25 bg-white px-7 py-4 font-medium text-ink hover:border-teal/50 transition-colors"
              >
                <Phone size={18} className="text-teal" /> {t("Call")} {site.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
